import * as RadPopover from '@radix-ui/react-popover'
import React, { useState } from 'react'
import styled from 'styled-components'

import {
  ArrowsDropdownIcon,
  Separator as Separator_,
} from '@root/shared/ui-kit'

import type { TopbarPositionsMode } from '../atoms'

type Props = {
  active: boolean
  items: Array<{
    value: TopbarPositionsMode
    label: React.ReactNode
  }>
  value: TopbarPositionsMode
  onChange: (value: TopbarPositionsMode) => void
}

export const TopbarSwitchDropdown: React.FC<Props> = ({
  active,
  items,
  value,
  onChange,
}) => {
  const [open, setOpen] = useState<boolean>(false)

  const handleSelect = (item: TopbarPositionsMode) => {
    onChange(item)
    setOpen(false)
  }

  const label = items.find((item) => item.value === value)?.label

  return (
    <Root open={open} onOpenChange={setOpen}>
      <Trigger open={open} onClick={() => setOpen(!open)} aria-checked={active}>
        <Label>{label}</Label>
        <Icon size={12} />
      </Trigger>
      <RadPopover.Portal>
        <Content side="bottom" align="center">
          <Item onClick={() => handleSelect(value)} aria-checked>
            {label}
            <Icon size={12} aria-checked />
          </Item>
          <Separator />
          {items
            .filter((item) => item.value !== value)
            .map((item) => (
              <Item onClick={() => handleSelect(item.value)} key={item.value}>
                {item.label}
              </Item>
            ))}
        </Content>
      </RadPopover.Portal>
    </Root>
  )
}

const Root = styled(RadPopover.Root)`
  position: relative;
`

const Trigger = styled(RadPopover.Trigger)<{
  open: boolean
}>`
  background: ${({ open }) => (open ? 'var(--backgroundA-2)' : 'transparent')};
  color: ${({ open }) => (open ? 'var(--text-12)' : 'var(--text-11)')};

  display: grid;
  overflow: hidden;
  align-items: center;
  cursor: pointer;
  position: relative;
  justify-content: space-between;

  border-radius: 8px;
  grid-template-columns: 1fr auto;
  border: 0;

  min-width: 60px;
  height: 100%;

  font-weight: 600;
  font-size: 11px;

  &:hover {
    color: var(--text-12);
  }
  &[aria-checked='true'] {
    color: var(--text-12);
    font-weight: 600;
  }
`

const Content = styled(RadPopover.Content)`
  background: var(--appearance-1);
  backdrop-filter: blur(35px);

  cursor: pointer;
  overflow: hidden;

  width: calc(var(--radix-popover-trigger-width) + 12px);
  border-radius: 8px;
  margin-top: -23px;
  box-shadow: 2px 2px 9px 1px rgb(50 50 50 / 30%);

  animation-duration: 400ms;
  animation-timing-function: cubic-bezier(0.16, 1, 0.3, 1);
`

const Item = styled.div`
  color: var(--text-11);
  font-weight: 600;

  background: none;
  transition: all 100ms ease-in;
  display: grid;
  align-items: center;
  text-align: left;
  justify-content: space-between;
  overflow: hidden;

  border: 0;
  width: 100%;
  padding-inline: 6px;
  grid-template-columns: 1fr auto;
  font-size: 11px;
  height: 24px;

  &:hover {
    color: var(--text-12);
  }
  &[aria-checked='true'] {
    color: var(--text-12);
    font-weight: 600;
  }
`

const Label = styled.span`
  gap: 3px;

  overflow: hidden;
  display: grid;

  text-align: left;
  text-decoration: none;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const Separator = styled(Separator_)`
  background: var(--backgroundA-4);
  margin: auto;
  width: 60px;
`

const Icon = styled(ArrowsDropdownIcon)`
  color: var(--textA-7);

  &[aria-checked='true'] {
    color: var(--text-12);
  }
`
